import React, { cloneElement, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Image } from '@/components/common/image';
import { ChevronRight } from 'lucide-react';
import { agentManager, iamTokenManager } from '@/manager';
import { useToast } from '@/hooks/use-toast';
import { AgentData } from '@/services/agent-list';
import { isAgentAvailableForUser } from '@/utils/agent-availability';
import { ENV } from '@/constant';
import { AiAgentImage } from '../login/assets/assets/AiAgentImage';
import { useLogout } from '@/hooks/use-logout';
import { IconButton } from '../common/icon-button';
import { LogoutIcon } from '../settings-menu/assets';

interface SelectSystemModelProps {
  trigger?: React.ReactElement;
  open?: boolean;
  agents?: AgentData[];
  title?: string;
  onConfirm?: (id: string[]) => void;
  onOpenChange?: (open: boolean) => void;
}

/** 选择系统弹窗 */
export const SelectSystemModel: React.FC<SelectSystemModelProps> = ({
  trigger,
  open: externalOpen,
  title,
  onConfirm,
  agents,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleLogout } = useLogout();

  const [internalOpen, setInternalOpen] = useState(false);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 进入系统（跳转到定制域名,只有主站和灰度主站,才会打开该弹窗）
  const handleSelect = (item: AgentData, isException: boolean) => {
    // 异常情况
    if (isException) {
      toast.default({ description: t('select_system.disabled_use') })
      return false;
    }
    const shareToken = iamTokenManager.getToken();

    // 线上环境,拦截没有定制域名的智能体
    if (ENV.isProdMainEnv || ENV.isGrayMainEnv) {
      if (!item.subDomain) {
        toast.default({ description: t('select_system.tips') })
        return false;
      }
      const domain = item.subDomain.replace(/\/+$/, '');
      agentManager.setAgentIdToCache(item.id, item.subDomain)
      location.href = `${domain}/agent/creation/${item.id}/?shareToken=${shareToken}&targetAgentId=${item.id}`
    } else {
      // 测试环境, 优先使用定制域名, 兜底用测试环境域名
      if (ENV.isTestMainEnv || ENV.isTestSubEnv) {
        const domain = item.subDomain ? item.subDomain?.replace(/\/+$/, '') : '';
        agentManager.setAgentIdToCache(item.id, domain || ENV.testMainEnvOrigin)
        location.href = `${domain || ENV.testMainEnvOrigin}/agent/creation/${item.id}/?shareToken=${shareToken}&targetAgentId=${item.id}`
        return
      }
      // 开发环境，只跳本地路由
      agentManager.setAgentIdToCache(item.id, location.origin)
      location.href = `${location.origin}/agent/creation/${item.id}/?shareToken=${shareToken}&targetAgentId=${item.id}`
    }
  }

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay
        className={cn("fixed inset-0 bg-[rgba(0,0,0,0.2)] backdrop-filter backdrop-blur z-50")}
      />
      <Dialog.Content
        modal-tag-move
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "flex-shrink-0",
          "z-[50] overflow-hidden",
          "w-[420px] max-h-[492px]  bg-white rounded-[24px] p-10",
          "[border-image-source:linear-gradient(180deg,_rgba(255,_255,_255,_0.5)_0%,_rgba(255,_255,_255,_0)_13.27%)] [border-image-slice:1] box-border bg-[rgba(255,_255,_255,_1)] [box-shadow:0px_4px_28px_rgba(29,_29,_31,_0.12)]",
          "outline-none"
        )}
      >
        <div className="flex h-full flex-col select-none overflow-hidden">
          {/* Logo */}
          <div className="relative flex items-center justify-center mb-[28px]">
            <AiAgentImage className='size-[48px]' />
            <IconButton
              className="absolute right-0"
              icon={<LogoutIcon className='size-5'/>}
              onClick={() => handleLogout()}
            />
          </div>
          <div className='flex flex-col overflow-y-auto scrollbar-hide'>
            <div className='text-sm text-[rgba(152,162,179,1)] mb-4'>{t('select_system.desc')}</div>
            <div className='w-full flex flex-col gap-4'>
              {!agents.length ? (
                <ListSkeleton />
              ) : (
                agents?.map((item, index) => {
                  const tags: string[] = [];
                  const isExpired = item.expireAt && Date.now() > item.expireAt * 1000
                  const canUse = isAgentAvailableForUser(item)

                  if (!canUse) {
                    tags.push(t('select_system.deactivate'));
                  }
                  if (isExpired) {
                    tags.push(t('select_system.expired'));
                  }
                  return (
                    <div
                      key={index}
                      className={cn(
                        'flex justify-between items-center border border-[rgba(237,237,237,1)] rounded-[16px] px-5 py-4 cursor-pointer active:scale-[0.98] transition-all duration-200 hover:bg-gray-50',
                        !canUse && "cursor-not-allowed"
                      )}
                      onClick={() => handleSelect(item, !canUse)}
                    >
                      <div className='flex items-center gap-2 min-w-0'>
                        <div className='size-10 rounded-full flex-shrink-0'>
                          <Image src={item.avatar} alt='avatar' className='size-full object-cover rounded-full' />
                        </div>
                        <div className='flex flex-col justify-between min-w-0'>
                          <div className='text-md text-[rgba(16,24,40,1)] truncate'>{item.name}</div>
                          <div className='text-xs text-[rgba(152,162,179,1)]'>{item.creatorName}</div>
                        </div>
                        {tags.map((tag, tagIndex) => (
                          <div
                            key={`${tag}-${tagIndex}`}
                            className='text-xs max-h-max text-[rgba(16,24,40,1)] bg-[rgba(16,24,40,0.05)] rounded-[6px] px-2 py-1 flex-shrink-0 self-start'
                          >
                            {tag}
                          </div>
                        ))}
                      </div>
                      {canUse && <div>
                        <ChevronRight className='size-[18px] text-[rgba(152,162,179,1)]' />
                      </div>}
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}

export const ListSkeleton: React.FC = () => {
  return (
    <>
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          aria-hidden
          className='flex justify-between items-center border border-[rgba(237,237,237,1)] rounded-[16px] px-5 py-4'
        >
          <div className='flex items-center gap-2 min-w-0'>
            <div className='size-10 rounded-full flex-shrink-0 bg-gray-200 animate-pulse' />
            <div className='flex flex-col justify-between min-w-0 gap-2'>
              <div className='h-4 w-28 bg-gray-200 rounded animate-pulse' />
              <div className='h-3 w-20 bg-gray-200 rounded animate-pulse' />
            </div>
          </div>
          <div className='size-[18px] bg-gray-200 rounded animate-pulse' />
        </div>
      ))}
    </>
  )
}
